import { ACHIEVEMENTS, achievementTitle } from '../content/achievements';
import { HUSTLES, hustleOpen, TIERS, type HustleId, type HustleTier } from '../content/hustles';
import { decorPoints } from '../content/decor';
import { FEED_AUTHORS, PLAYER_STATUSES } from '../content/facespace';
import { byBody, isWoman } from '../content/looks';
import type { SfxName } from '../content/types';
import { formatMoney } from '../core/math';
import type { Random } from '../core/Random';
import type { Store } from '../state/Store';
import { formatClock, rankFor } from '../state/rules';
import type { CounterId, FeedPost } from '../state/types';
import { controlsHelp } from './controlsHelp';
import { clear, h, layer } from './dom';
import { settingsControls } from './settingsForm';

export interface Settings {
  music: number;
  sfx: number;
  shadows: boolean;
  sway: boolean;
  /** See the park through your own eyes (V toggles it). */
  firstPerson: boolean;
  /** Push up to look down (camera and aiming, on any controls). */
  invertY: boolean;
}

export interface PhoneHost {
  store: Store;
  rng: Random;
  portrait(id: string): string | undefined;
  sfx(name: SfxName): void;
  onOpen(): void;
  onClose(): void;
  onSave(): void;
  onQuit(): void;
  settings(): Settings;
  onSettings(s: Settings): void;
  /** The Online tab (multiplayer). */
  online: { mount(el: HTMLElement): void; focusChat(): void };
  /** The Hustles tab: "show me" points the objective arrow at where one starts. */
  hustles: { show(id: HustleId): void };
}

export type PhoneTab = 'feed' | 'hustles' | 'online' | 'trophies' | 'stats' | 'help' | 'settings';

const STAT_LABELS: Array<[CounterId, string]> = [
  ['beers', ':beer: Beers cracked'],
  ['cansRecycled', ':empty-can: Cans recycled'],
  ['scrapSold', ':scrap: Scrap sold'],
  ['scratchers', ':scratcher: Scratchers scratched'],
  ['scratchWins', ':trophy: Scratcher wins'],
  ['stunts', ':cartwheel: Stunts landed'],
  ['stuntFails', ':ambulance: Stunts failed'],
  ['jobs', ':hammer: Odd jobs done'],
  ['fireworks', ':fireworks: Fireworks lit'],
  ['tornadoes', ':tornado: Tornadoes survived'],
  ['passOuts', ':knocked-out: Times passed out'],
  ['rentPaid', ':trailer: Weeks of rent paid'],
  ['tv', ':tv: TV shows watched'],
  ['dumpsterDives', ':trash-can: Dumpster dives'],
  ['donuts', ':donut: Donuts (the car kind)'],
];

export class Phone {
  readonly el: HTMLDivElement;
  private readonly body: HTMLDivElement;
  private readonly tabsEl: HTMLDivElement;
  private readonly clock: HTMLSpanElement;
  private tab: PhoneTab = 'feed';
  private open = false;
  private lastSeen = 0;

  constructor(
    parent: HTMLElement,
    private readonly host: PhoneHost,
  ) {
    this.el = layer();
    this.body = h('div', { class: 'phone-body' });
    this.tabsEl = h('div', { class: 'tabs', attrs: { role: 'tablist' } });
    this.clock = h('span');
    const screen = h(
      'div',
      { class: 'phone-screen' },
      h('div', { class: 'phone-bar' }, this.clock, h('span', { text: ':signal: 1 bar · :battery: 4%' })),
      h(
        'div',
        { class: 'fs-header' },
        h('span', { text: 'facespace' }),
        h('button', {
          class: 'phone-btn secondary',
          text: '✕',
          style: { width: 'auto', marginTop: '0', padding: '4px 10px' },
          attrs: { 'aria-label': 'Close phone' },
          on: { click: () => this.close() },
        }),
      ),
      this.tabsEl,
      this.body,
    );
    const phone = h(
      'div',
      { class: 'phone', attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Phone' } },
      screen,
      h('div', { class: 'phone-crack' }),
    );
    this.el.append(phone);
    this.el.addEventListener('click', (e) => {
      if (e.target === this.el) this.close();
    });
    parent.append(this.el);
    window.addEventListener(
      'keydown',
      (e) => {
        if (!this.open) return;
        const tag = (e.target as HTMLElement).tagName;
        // Typing a chat message: only Escape closes the phone.
        if (tag === 'INPUT' && (e.target as HTMLInputElement).type === 'text') {
          if (e.key === 'Escape') {
            e.preventDefault();
            this.close();
          }
          return;
        }
        const typing = tag === 'SELECT';
        if (e.key === 'Escape' || e.code === 'KeyP' || e.code === 'Tab') {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.close();
        } else if (!typing && ['KeyE', 'Space', 'KeyQ', 'KeyF', 'KeyR', 'KeyT', 'KeyG'].includes(e.code)) {
          e.stopImmediatePropagation();
        }
      },
      true,
    );
  }

  get isOpen(): boolean {
    return this.open;
  }

  get unread(): number {
    return this.host.store.state.feed.filter((p) => p.id > this.lastSeen).length;
  }

  toggle(tab?: PhoneTab): void {
    if (this.open) this.close();
    else this.show(tab);
  }

  show(tab: PhoneTab = this.tab): void {
    if (!this.open) this.host.onOpen();
    this.open = true;
    this.tab = tab;
    this.el.hidden = false;
    this.host.sfx('phone');
    this.render();
  }

  /** Opens the Online tab with the cursor in the chat box. */
  openChat(): void {
    this.show('online');
    this.host.online.focusChat();
  }

  close(): void {
    if (!this.open) return;
    // Don't leave focus in a hidden text box, where it would swallow game keys.
    if (this.el.contains(document.activeElement)) (document.activeElement as HTMLElement).blur();
    this.open = false;
    this.el.hidden = true;
    this.host.onClose();
  }

  private render(): void {
    const s = this.host.store;
    this.clock.textContent = formatClock(s.minutes);
    clear(this.tabsEl);
    const tabs: Array<[PhoneTab, string]> = [
      ['feed', ':newspaper: Feed'],
      ['hustles', ':briefcase: Hustles'],
      ['online', ':globe: Online'],
      ['trophies', ':trophy: Trophies'],
      ['stats', ':bar-chart: Stats'],
      ['help', ':question: Help'],
      ['settings', ':gear:'],
    ];
    for (const [id, label] of tabs) {
      const btn = h('button', {
        class: this.tab === id ? 'on' : '',
        text: label,
        attrs: {
          role: 'tab',
          'aria-selected': String(this.tab === id),
          ...(id === 'settings' ? { 'aria-label': 'Settings' } : {}),
        },
        on: {
          click: () => {
            this.tab = id;
            this.render();
          },
        },
      });
      this.tabsEl.append(btn);
      // On a narrow phone the tab bar scrolls: keep the open tab in view.
      if (this.tab === id)
        requestAnimationFrame(() => btn.scrollIntoView({ block: 'nearest', inline: 'nearest' }));
    }
    clear(this.body);
    this.body.scrollTop = 0;
    switch (this.tab) {
      case 'feed':
        this.renderFeed();
        break;
      case 'hustles':
        this.renderHustles();
        break;
      case 'online':
        this.host.online.mount(this.body);
        break;
      case 'trophies':
        this.renderTrophies();
        break;
      case 'stats':
        this.renderStats();
        break;
      case 'help':
        this.renderHelp();
        break;
      case 'settings':
        this.renderSettings();
        break;
    }
  }

  private avatar(post: FeedPost): HTMLElement {
    const img = this.host.portrait(post.avatar);
    if (img) return h('img', { class: 'avatar', attrs: { src: img, alt: '' } });
    const a = FEED_AUTHORS[post.avatar];
    return h('div', {
      class: 'avatar',
      text: a?.icon ?? ':person:',
      style: { background: a?.color ?? '#999' },
    });
  }

  private renderFeed(): void {
    const s = this.host.store;
    this.lastSeen = s.state.nextPostId - 1;
    const select = h('select', { attrs: { 'aria-label': "What's on your mind?" } });
    PLAYER_STATUSES.forEach((st, i) =>
      select.append(
        h('option', {
          attrs: { value: String(i) },
          text: st.meme ? `:picture: ${st.meme.top} / ${st.meme.bottom}` : st.text,
        }),
      ),
    );
    const posted = s.doneToday('post');
    const postBtn = h('button', {
      text: posted ? 'You already posted today (people are talking)' : 'Post',
      disabled: posted,
      on: {
        click: () => {
          const st = PLAYER_STATUSES[Number(select.value)];
          if (!st) return;
          const rating = s.rating();
          const likes = this.host.rng.int(2, 12) + Math.floor(rating / 4);
          s.markToday('post');
          s.incr('posts');
          s.post({ author: s.state.player.name, avatar: 'player', text: st.text, meme: st.meme, likes });
          const clout = s.addClout(Math.max(1, Math.round(likes / 10)));
          s.toast(`Your post got ${likes} likes! +${clout} Clout`, 'good', ':thumbs-up:');
          this.host.sfx('ding');
          this.render();
        },
      },
    });
    this.body.append(
      h(
        'div',
        { class: 'composer' },
        h('div', { style: { fontWeight: '700', marginBottom: '6px' }, text: "What's on your mind?" }),
        select,
        postBtn,
      ),
    );
    if (!s.state.feed.length)
      this.body.append(h('div', { class: 'post', text: 'Nobody has posted yet. Suspicious.' }));
    for (const p of s.state.feed) this.body.append(this.renderPost(p));
  }

  private renderPost(p: FeedPost): HTMLElement {
    const likeBtn = h('button', {
      class: p.liked ? 'liked' : '',
      text: `:thumbs-up: ${p.liked ? 'Liked' : 'Like'}`,
      on: {
        click: () => {
          p.liked = !p.liked;
          p.likes += p.liked ? 1 : -1;
          this.render();
        },
      },
    });
    return h(
      'div',
      { class: 'post' },
      h(
        'div',
        { class: 'post-head' },
        this.avatar(p),
        h(
          'div',
          {},
          h('div', { class: 'post-author', text: p.author }),
          h('div', { class: 'post-time', text: `Day ${p.day} · ${formatClock(p.minutes)} · :globe:` }),
        ),
      ),
      h('div', { class: 'post-text', text: p.text }),
      p.meme
        ? h(
            'div',
            { class: 'post-meme', style: { background: p.meme.bg } },
            h('div', { class: 'cap top', text: p.meme.top }),
            p.meme.icon,
            h('div', { class: 'cap bottom', text: p.meme.bottom }),
          )
        : null,
      h(
        'div',
        { class: 'post-actions' },
        likeBtn,
        h('span', { text: `${p.likes} likes` }),
        h('span', { text: ':speech: Comment (disabled by admin)' }),
      ),
    );
  }

  private renderHustles(): void {
    const st = this.host.store.state;
    this.body.append(
      h('h3', { text: ':briefcase: Ways to Make a Buck' }),
      h('div', {
        class: 'hustle-intro',
        text: 'From couch-cushion money to running a business. Do the easy ones and the big ones open up.',
      }),
    );
    for (const tier of [1, 2, 3] as HustleTier[]) {
      this.body.append(
        h(
          'div',
          { class: 'hustle-tier' },
          h('strong', { text: TIERS[tier].name }),
          h('span', { text: TIERS[tier].blurb }),
        ),
      );
      for (const def of HUSTLES.filter((d) => d.tier === tier)) {
        const open = hustleOpen(st, def.id);
        const done = def.done(st);
        const card = h(
          'div',
          { class: `hustle ${open ? '' : 'locked'}` },
          h(
            'div',
            { class: 'hustle-head' },
            h('span', { class: 'hustle-icon', text: open ? def.icon : ':lock:' }),
            h('span', { class: 'hustle-name', text: def.name }),
            def.fresh && open && done === 0 ? h('span', { class: 'hustle-new', text: 'NEW' }) : null,
          ),
          h('div', { class: 'hustle-pay', text: `${def.pay} · ${def.time}` }),
          h('div', { class: 'hustle-blurb', text: def.blurb }),
        );
        if (open) {
          card.append(h('div', { class: 'hustle-how', text: `:play: ${def.how}` }));
          const foot = h('div', { class: 'hustle-foot' }, h('span', { text: `Done: ${done}` }));
          if (def.pin)
            foot.append(
              h('button', {
                class: 'phone-btn secondary hustle-go',
                text: ':pin: Show me',
                on: {
                  click: () => {
                    this.host.hustles.show(def.id);
                    this.close();
                  },
                },
              }),
            );
          card.append(foot);
        } else {
          for (const n of def.needs) {
            const have = Math.min(n.need, n.have(st));
            card.append(
              h(
                'div',
                { class: 'hustle-need' },
                h('span', {
                  text: `${have >= n.need ? ':check:' : ':empty:'} ${n.label}: ${have}/${n.need}`,
                }),
                h(
                  'div',
                  { class: 'hustle-bar' },
                  h('div', { style: { width: `${Math.round((have / n.need) * 100)}%` } }),
                ),
              ),
            );
          }
        }
        this.body.append(card);
      }
    }
  }

  private renderTrophies(): void {
    const s = this.host.store;
    const got = s.state.achievements;
    this.body.append(
      h('h3', { text: `You Might Be a Redneck If... (${got.length}/${ACHIEVEMENTS.length})` }),
    );
    for (const a of ACHIEVEMENTS) {
      const unlocked = got.includes(a.id);
      this.body.append(
        h(
          'div',
          { class: `trophy ${unlocked ? '' : 'locked'}` },
          h('div', { class: 't-icon', text: unlocked ? ':trophy:' : ':lock:' }),
          h(
            'div',
            {},
            h('div', {
              class: 't-title',
              text: unlocked ? achievementTitle(a, isWoman(s.state.player.look)) : '???',
            }),
            h('div', {
              class: 't-line',
              text: unlocked
                ? `...if ${a.line.replace(/^\.\.\./, '')}`
                : "Keep livin' the dream to find out.",
            }),
          ),
        ),
      );
    }
  }

  private renderStats(): void {
    const s = this.host.store;
    const st = s.state;
    const rating = s.rating();
    const { rank, next } = rankFor(rating, isWoman(st.player.look));
    this.body.append(
      h('h3', { text: `:medal: ${rank.title}` }),
      h('div', { style: { fontFamily: 'var(--font-hand)', marginBottom: '8px' }, text: rank.blurb }),
      next
        ? h('div', {
            style: { fontSize: '13px', marginBottom: '8px' },
            text: `Next rank: ${next.title} at ${next.min} pts (${next.min - rating} to go)`,
          })
        : h('div', {
            text: `You have reached the top. Long live the ${byBody(st.player.look, 'king', 'queen')}.`,
          }),
    );
    const rows: Array<[string, string]> = [
      [':star: Redneck Rating', String(rating)],
      ['   from Clout', String(st.clout)],
      ['   from Yard Decor', String(decorPoints(st.decor))],
      ['   from Trophies', String(st.achievements.length * 2)],
      ['   from a running Camaro', st.camaro >= 100 ? '15' : '0'],
      [':cash: Cash', formatMoney(st.money)],
      [':car: Camaro restoration', `${Math.round(st.camaro)}%`],
      [':calendar: Days in Lucky Acres', String(st.time.day)],
    ];
    for (const [id, label] of STAT_LABELS) rows.push([label, String(st.counters[id] ?? 0)]);
    for (const [k, v] of rows)
      this.body.append(h('div', { class: 'stat-row' }, h('span', { text: k }), h('strong', { text: v })));
  }

  private renderHelp(): void {
    this.body.append(...controlsHelp());
  }

  private renderSettings(): void {
    this.body.append(
      h('h3', { text: ':gear: Settings' }),
      ...settingsControls(this.host.settings(), (set) => this.host.onSettings(set)),
      h('button', {
        class: 'phone-btn',
        text: ':save: Save game',
        on: {
          click: () => {
            this.host.onSave();
            this.host.store.toast('Game saved. Probably.', 'good', ':save:');
          },
        },
      }),
      h('button', {
        class: 'phone-btn danger',
        text: ':door: Quit to title',
        on: {
          click: () => {
            if (window.confirm('Quit to the title screen? Your game is saved automatically.')) {
              this.close();
              this.host.onQuit();
            }
          },
        },
      }),
      h('div', {
        style: { fontSize: '12px', marginTop: '14px', color: '#606770' },
        text: 'Double Wide Dreams is an affectionate parody. Any resemblance to your cousin is purely coincidental.',
      }),
    );
  }
}
